import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Language } from '@shared/types';

/**
 * LanguageSelector Component
 * 
 * Allows users to switch between Hungarian (HU) and English (EN) languages.
 * Updates i18n context and provides visual feedback for the active language.
 * 
 * Requirements:
 * - FR-002: Multi-language support (Hungarian, English)
 * - UI-001: Language selection on homepage
 */

interface LanguageSelectorProps {
  /**
   * Optional CSS classes to apply to the container
   */
  className?: string;
  
  /**
   * Optional callback when language changes
   */
  onLanguageChange?: (language: Language) => void;
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  className = '',
  onLanguageChange,
}) => {
  const { t, i18n } = useTranslation();
  
  const currentLanguage = i18n.language as Language;

  /**
   * Handles language change
   * Updates i18n context and triggers optional callback
   */
  const handleLanguageChange = async (language: Language) => {
    if (language === currentLanguage) {
      return; // Already selected
    }

    try {
      await i18n.changeLanguage(language);
      onLanguageChange?.(language);
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  return (
    <div className={`flex items-center gap-3 ${className}`} role="group" aria-label={t('languageSelector.ariaLabel')}>
      {/* Label */}
      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
        {t('languageSelector.label')}
      </span>

      {/* Language Buttons */}
      <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-1">
        {/* Hungarian Button */}
        <button
          type="button"
          onClick={() => handleLanguageChange('HU')}
          className={`
            px-4 py-2 text-sm font-medium rounded-md transition-all duration-200
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
            ${
              currentLanguage === 'HU'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }
          `}
          aria-pressed={currentLanguage === 'HU'}
          aria-label={t('languageSelector.hungarian')}
        >
          🇭🇺 {t('languageSelector.hungarian')}
        </button>

        {/* English Button */}
        <button
          type="button"
          onClick={() => handleLanguageChange('EN')}
          className={`
            px-4 py-2 text-sm font-medium rounded-md transition-all duration-200
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
            ${
              currentLanguage === 'EN'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }
          `}
          aria-pressed={currentLanguage === 'EN'}
          aria-label={t('languageSelector.english')}
        >
          🇬🇧 {t('languageSelector.english')}
        </button>
      </div>

      {/* Current Language Indicator (Screen reader only) */}
      <span className="sr-only" aria-live="polite">
        {t('languageSelector.currentLanguage', { language: currentLanguage })}
      </span>
    </div>
  );
};
